import { defineStore } from 'pinia';

import AuthService from '@/services/auth.service.js';
import UserService from '@/services/user.service.js';

let refreshTimer = null;

function getTokenExpiry(jwtToken) {
    try {
        let base64Url = jwtToken.split('.')[1];
        if (!base64Url) return null;

        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

        while (base64.length % 4) {
            base64 += '=';
        }

        const payload = JSON.parse(atob(base64));

        return typeof payload.exp === 'number'
            ? payload.exp * 1000
            : null;
    } catch (err) {
        return null;
    }
}

export const useAuthStore = defineStore('auth', {

    state: () => ({
        user: null,

        accessToken: localStorage.getItem('access_token') || null,

        refreshToken: localStorage.getItem('refresh_token') || null,
    }),

    getters: {

        isAuthenticated: (state) => !!state.accessToken,

    },

    actions: {

        async login(credentials) {

            const response = await AuthService.login(credentials);

            const accessToken = response.access_token;
            const refreshToken = response.refresh_token;

            if (!accessToken) {
                throw new Error(
                    'No se recibió access_token del servidor'
                );
            }

            if (!refreshToken) {
                throw new Error(
                    'No se recibió refresh_token del servidor'
                );
            }

            this.user = response.user;

            this.accessToken = accessToken;
            this.refreshToken = refreshToken;

            localStorage.setItem(
                'access_token',
                accessToken
            );

            localStorage.setItem(
                'refresh_token',
                refreshToken
            );

            this.scheduleTokenRefresh();

            return response;
        },

        async register(userData) {

            const response = await AuthService.register(userData);

            const accessToken = response.access_token;
            const refreshToken = response.refresh_token;

            if (!accessToken) {
                throw new Error(
                    'No se recibió access_token del servidor'
                );
            }

            if (!refreshToken) {
                throw new Error(
                    'No se recibió refresh_token del servidor'
                );
            }

            this.user = response.user;

            this.accessToken = accessToken;
            this.refreshToken = refreshToken;

            localStorage.setItem(
                'access_token',
                accessToken
            );

            localStorage.setItem(
                'refresh_token',
                refreshToken
            );

            this.scheduleTokenRefresh();

            return response;
        },

        // `changes` solo debe llevar los campos que realmente cambian
        // (nickname y/o email). Tras el PATCH, volvemos a pedir el
        // usuario con GET /auth/me en vez de fiarnos de la respuesta
        // del PATCH, para que `user` quede igual que lo que hay
        // realmente en el backend.
        async updateProfile(changes) {

            await UserService.updateProfile(changes);

            this.user = await AuthService.me();

            return this.user;
        },

        setTokens({
            access_token,
            refresh_token,
        }) {

            this.accessToken = access_token;
            this.refreshToken = refresh_token;

            localStorage.setItem(
                'access_token',
                access_token
            );

            if (refresh_token) {
                localStorage.setItem(
                    'refresh_token',
                    refresh_token
                );
            }

            this.scheduleTokenRefresh();
        },

        // Refresco proactivo: el access_token de JWT lleva un `exp` en
        // milisegundos, así que tenemos el momento exacto en que caduca.
        // Programamos un refresco unos segundos antes para que la sesión
        // siga viva sin esperar a que una petición falle con 401. Si el
        // refresh_token ya ha caducado, la renovación falla y cerramos
        // sesión (el listón de expiración real de la sesión es el refresh).
        scheduleTokenRefresh() {

            if (refreshTimer) {
                clearTimeout(refreshTimer);
                refreshTimer = null;
            }

            if (!this.accessToken || !this.refreshToken) return;

            const expiry = getTokenExpiry(this.accessToken);

            if (expiry === null) return;

            const refreshMarginMs = 5000;

            const delay = Math.max(
                0,
                expiry - Date.now() - refreshMarginMs
            );

            refreshTimer = setTimeout(async () => {

                refreshTimer = null;

                try {

                    const response = await AuthService.refresh(
                        this.refreshToken
                    );

                    const accessToken = response.access_token;
                    const refreshToken = response.refresh_token;

                    if (!accessToken || !refreshToken) {
                        throw new Error(
                            'El servidor no devolvió tokens válidos'
                        );
                    }

                    this.setTokens({
                        access_token: accessToken,
                        refresh_token: refreshToken,
                    });

                } catch (err) {

                    this.logout();

                }
            }, delay);
        },

        // Rellena `user` a partir del access_token guardado. Pensada para
        // llamarse al arrancar la app cuando ya hay sesión (token en
        // localStorage) pero `user` está vacío porque venimos de recargar
        // la página, no de un login/register en esta misma carga.
        async fetchUser() {

            if (!this.accessToken) return;

            try {
                this.user = await AuthService.me();
                this.scheduleTokenRefresh();
            } catch (err) {
                // Si el token ya no es válido (y el interceptor no ha
                // podido renovarlo con el refresh_token), no dejamos un
                // estado a medias: cerramos sesión limpia.
                this.logout();
            }
        },

        logout() {

            if (refreshTimer) {
                clearTimeout(refreshTimer);
                refreshTimer = null;
            }

            this.user = null;

            this.accessToken = null;
            this.refreshToken = null;

            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            
        },
    },
});